import React, { useState, useEffect, ReactNode } from 'react'
import { RadioChangeEvent } from 'antd/lib/radio'
import { Radio } from 'antd'

import FormattedMessage from 'components/FormattedMessage'
import Hint from 'components/Hint'

import { createProjectUrl } from 'helpers/nexus'

import { CurrentUser } from 'generated/types'

import AddToProject from './AddToProject'
import CreateProject from './CreateProject'

import './FormSwitcher.less'

const FormSwitcher: React.FC<FormSwitcherProps> = (props) => {
  const { defaultMode = 'ADD_TO_PROJECT', currentUser } = props
  const [mode, setMode] = useState(defaultMode)
  const isDashboardIntegratedWithNexus = currentUser?.preferredPartner?.settings.isDashboardIntegratedWithNexus
  const scroll = (mode: FormSwitcherMode) => {
    const element = document.getElementById(mode)

    element && element.scrollIntoView()
  }

  useEffect(() => {
    scroll(mode)
  }, [mode])

  const switchHandler = (event: RadioChangeEvent) => {
    const selectedMode = event.target.value

    setMode(selectedMode)

    if (selectedMode === 'NEW_PROJECT' && isDashboardIntegratedWithNexus) {
      window.location.href = createProjectUrl(props.provider.id)
    }
  }

  const {
    isSignedIn,
    preferredPartner,
  } = props.currentUser

  const canCreateNewProject = isSignedIn && !preferredPartner?.settings.hideProjectCrudUi

  return (
    <div className="contact-request__form-switcher">
      <h1 className="contact-request__form-switcher__title">
        <FormattedMessage id="contactRequest.formSwitcher.title" />
      </h1>

      <Radio.Group onChange={switchHandler} defaultValue={mode}>
        <div className="contact-request__form-switcher__item" id="ADD_TO_PROJECT">
          <div className="contact-request__form-switcher__item__head">
            <Radio value="ADD_TO_PROJECT">
              <FormattedMessage id="contactRequest.formSwitcher.switch.addToProject" />
            </Radio>
          </div>

          {mode === 'ADD_TO_PROJECT' && (
            <div className="contact-request__form-switcher__item__content">
              <AddToProject
                projects={props.projects}
                currentUser={props.currentUser}
                onSuccessCallback={props.onSuccessCallback}
                initialData={props.initialData}
                provider={props.provider}
              />
            </div>
          )}
        </div>

        <div className="contact-request__form-switcher__item" id="NEW_PROJECT">
          <div className="contact-request__form-switcher__item__head">
            <Radio value="NEW_PROJECT" disabled={!canCreateNewProject}>
              <FormattedMessage id="contactRequest.formSwitcher.switch.newProject" />

              {!isSignedIn && (
                <Hint className="contact-request__form-switcher__item__head__hint">
                  <FormattedMessage
                    id="requestForm.loginHint"
                    values={{
                      loginLink: (chunks: ReactNode) => <a className="link" href="/login">{chunks}</a>,
                      registrationLink: (chunks: ReactNode) => <a className="link" href="/password/new">{chunks}</a>,
                    }}
                  />
                </Hint>
              )}

              {preferredPartner?.settings.hideProjectCrudUi && (
                <Hint className="contact-request__form-switcher__item__head__hint">
                  <FormattedMessage id="requestForm.hint.featureHidden" />
                </Hint>
              )}
            </Radio>
          </div>

          {mode === 'NEW_PROJECT' && !isDashboardIntegratedWithNexus && (
            <div className="contact-request__form-switcher__item__content">
              <CreateProject {...props} />
            </div>
          )}

          {mode === 'NEW_PROJECT' && isDashboardIntegratedWithNexus && (
            <div className="contact-request__form-switcher__item__content">
              <FormattedMessage id="contactRequest.formSwitcher.switch.newProject.redirectingToNexus" />
            </div>
          )}
        </div>
      </Radio.Group>
    </div>
  )
}

interface FormSwitcherProps extends React.ComponentProps<typeof AddToProject> {
  defaultMode?: FormSwitcherMode
  currentUser: CurrentUser
  switcherCallback?: (mode: FormSwitcherMode) => void
}

type FormSwitcherMode = 'NEW_PROJECT' | 'ADD_TO_PROJECT'

export default FormSwitcher
